import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  AppBar,
  Toolbar,
  Alert,
} from '@mui/material';
import {
  Build as BuildIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, you would send this to a backend
    setSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 2 }}>
        <Toolbar>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/logo.png"
              alt="Congrow Logo"
              style={{ height: '60px', marginRight: '16px', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
          </Box>

          {/* Navigation Links */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button color="primary" onClick={() => navigate('/about')}>
              About Us
            </Button>
            <Button color="primary" onClick={() => navigate('/contact')}>
              Contact
            </Button>
            <Button
              color="primary"
              startIcon={<BuildIcon />}
              onClick={() => navigate('/login?role=contractor')}
            >
              Contractor
            </Button>
            <Button
              color="primary"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/login?role=homeowner')}
            >
              Homeowner
            </Button>
            <Button
              color="primary"
              startIcon={<AdminIcon />}
              onClick={() => navigate('/login?role=admin')}
            >
              Admin
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        sx={{
          minHeight: '100vh',
          background: '#003d5c',
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px, 100px 100px, 10px 10px, 10px 10px',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Paper elevation={10} sx={{ p: { xs: 3, md: 6 }, borderRadius: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              textAlign="center"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Contact Us
            </Typography>
            <Typography variant="h6" textAlign="center" color="text.secondary" paragraph>
              Get in touch with our team
            </Typography>

            {submitted && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Thank you for your message! We'll get back to you soon.
              </Alert>
            )}

            <Grid container spacing={4} sx={{ mt: 2 }}>
              {/* Contact Information */}
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 4 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <EmailIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        Email
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        info@congrow.com
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PhoneIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        Phone
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        (555) 123-4567
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LocationIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        Address
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        123 Construction Way
                        <br />
                        Builder City, BC 12345
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Contact Form */}
              <Grid item xs={12} md={8}>
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        type="email"
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        multiline
                        rows={6}
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ mt: 3 }}
                    fullWidth
                  >
                    Send Message
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Box textAlign="center" mt={4}>
              <Button variant="outlined" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Contact;
